import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { XCloseIcon } from '@rsos/assets/icons';
import { Box } from '@rsos/base-components';
import Checkbox from '@rsos/components/capstone/base/Checkbox';
import { ESRI } from '@rsos/constants/mapProviders';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import {
  COMPANION_FOOTPRINT_TITLE,
  COMPANION_IMAGE_TITLE,
  GISServiceTypes,
  GISStateLayer,
  gisLayerFor,
  gisSublayerFor,
  isCompanionFootprintTitle,
  isCompanionImageTitle,
  isCompanionRoleTitle,
  loadGISLayer,
  updateLayer,
} from '@rsos/reduxDataStore';
import { notifyError } from '@rsos/utils/toastUtils';
import useIsIRP from '@rsos/utils/useIsIRP';
import type { RootState } from '../../../app/rootReducer';
import { trackToggleSupplementalLayer } from '../../JurisdictionView/Map/gainsight/gisTrackings';
import { CompanionLevelPicker, hasWmsCompanion } from './CompanionLevelPicker';
import { CompanionRoleControl } from './CompanionRoleControl';
import { GISLayerAccordion } from './GISLayerAccordion';
import {
  LayerControlRow,
  RegularCheckboxWrapper,
  RemoveLayerButton,
} from './GISLayerControls.styles';
import { GISSublayerControl } from './GISSublayerControl';

interface GISLayerControlProps {
  layer: GISStateLayer;
  onRemove?: () => void;
}

/**
 * A checkbox for one GIS layer; expandable accordion for layers with sublayers.
 * Loads layers on-demand when user first clicks them, then toggles visibility.
 * Directly toggles visibility on the ArcGIS layer for immediate feedback,
 * then syncs Redux state.
 */
export const GISLayerControl = memo<GISLayerControlProps>(
  ({ layer, onRemove }) => {
    const mapProvider = useSelector(
      (state: RootState) => state.sinatra?.ui?.mapProvider,
    );
    const isLoading = layer.loading;
    // Disable only when not using ESRI or when layer is currently loading
    const isDisabled = mapProvider !== ESRI || isLoading;
    const dispatch =
      useDispatch<ThunkDispatch<RootState, undefined, UnknownAction>>();
    const { irpVersion } = useIsIRP();

    const filteredSublayerIds = useMemo(
      () => layer.filteredSublayerIds || [],
      [layer.filteredSublayerIds],
    );

    // The basemap is controlled in the lower-left "basemap menu"; only show it
    // here if it has toggleable sublayers.
    const isBasemapWithoutSublayers =
      layer.service.service_type === GISServiceTypes.basemap &&
      layer.sublayers.length <= 1;

    const reportLoadError = useCallback(
      (message: string) => {
        notifyError({
          message,
          options: { toastId: `gis-layer-load-${layer.id}` },
        });
      },
      [layer.id],
    );

    const toggle = useCallback(() => {
      // If layer is not loaded yet, load it on-demand and set to visible
      if (!layer.loaded && !layer.loading) {
        dispatch(loadGISLayer({ service: layer.service }))
          .unwrap()
          .then((result: { layerId: string; loadedLayer: GISStateLayer }) => {
            const { loadedLayer } = result;

            // After loading, set layer and sublayers to visible directly
            // We need to do this because the useEffect may not trigger immediately
            const gisLayer = gisLayerFor(loadedLayer);
            if (gisLayer) {
              gisLayer.visible = true;

              // Set sublayer visibility - excluded ones stay hidden, others become visible
              const excludedIds = loadedLayer.filteredSublayerIds || [];
              loadedLayer.sublayers.forEach(sl => {
                const gisSublayer = gisSublayerFor(sl);
                if (!gisSublayer) return;

                // Keep filtered sublayers always invisible (matches existing toggle behavior)
                if (excludedIds.includes(sl.uniqueId)) {
                  gisSublayer.visible = false;
                } else {
                  gisSublayer.visible = true;
                }
              });
            }

            // Update Redux state
            dispatch(
              updateLayer({
                layerID: layer.id,
                active: true,
              }),
            );

            // Analytics tracking for first load
            gspx.trackEvent({
              name: 'map_turn_on_gis_layer',
              type: GPX_EVENT_TYPES.INFO,
              feature: GPX_FEATURES.LEGACY,
              capability: GPX_CAPABILITIES.LEGACY,
              route: GPX_ROUTES.MAP,
              additionalKeys: {
                legacy_category: 'Data Map',
                legacy_name: `Turn on ${layer.title}`,
                Category: 'GIS Service',
                'Launched date': new Date(),
                irpVersion,
                layerTitle: layer.title,
              },
            });

            trackToggleSupplementalLayer({
              layerName: layer.title,
              isVisible: true,
              layerCategory: 'gis_service',
            });
          })
          .catch((error: Error) => {
            const detail = error?.message || 'Failed to load layer.';
            reportLoadError(`Couldn't load ${layer.title}: ${detail}`);
            // For on-demand layers, remove broken placeholders so users
            // don't keep clicking a row that can never activate.
            if (onRemove) {
              onRemove();
            }
          });
        return;
      }

      // Service layer visibility depends on layer type
      // For layers with sublayers, parent is visible if at least one sublayer is active
      // For layers without sublayers, parent visibility is toggled directly
      const gisLayer = gisLayerFor(layer);
      if (!gisLayer) {
        // Layer is marked loaded but has no ArcGIS layer object (transient load failure).
        // Retry loading instead of silently returning.
        dispatch(loadGISLayer({ service: layer.service }))
          .unwrap()
          .then((result: { layerId: string; loadedLayer: GISStateLayer }) => {
            const { loadedLayer } = result;
            const retryLayer = gisLayerFor(loadedLayer);
            if (retryLayer) {
              retryLayer.visible = true;
              const excludedIds = loadedLayer.filteredSublayerIds || [];
              loadedLayer.sublayers.forEach(sl => {
                const gisSublayer = gisSublayerFor(sl);
                if (!gisSublayer) return;
                gisSublayer.visible = !excludedIds.includes(sl.uniqueId);
              });
            }
            dispatch(updateLayer({ layerID: layer.id, active: true }));
          })
          .catch((error: Error) => {
            const detail = error?.message || 'Failed to reload layer.';
            reportLoadError(`Couldn't load ${layer.title}: ${detail}`);
            if (onRemove) {
              onRemove();
            }
          });
        return;
      }

      // Get valid sublayers (excluding filtered ones)
      const validSublayers = layer.sublayers.filter(
        sl => !filteredSublayerIds.includes(sl.uniqueId),
      );

      // Check if at least one valid sublayer is currently active
      // For WMS/WMTS, also check actual ArcGIS sublayer visibility, not just Redux state
      const isWMSorWMTS =
        layer.service.service_type === GISServiceTypes.wms ||
        layer.service.service_type === GISServiceTypes.wmts;

      let atLeastOneActive = false;
      if (isWMSorWMTS) {
        // For WMS/WMTS, check actual ArcGIS sublayer visibility
        const activeSublayers = validSublayers.filter(sl => {
          const gisSublayer = gisSublayerFor(sl);
          return gisSublayer && gisSublayer.visible;
        });
        atLeastOneActive = activeSublayers.length > 0;
      } else {
        // For other layers, check Redux state
        atLeastOneActive =
          validSublayers.length > 0 && validSublayers.some(sl => sl.active);
      }

      const newSublayerState = !atLeastOneActive; // Toggle: if at least one on, turn all off; otherwise turn all on

      // Handle layers without sublayers (like WMS/WMTS with 0 sublayers)
      if (validSublayers.length === 0) {
        // For layers without sublayers, toggle the layer itself
        const newVisible = !layer.active;
        gisLayer.visible = newVisible;
        dispatch(
          updateLayer({
            layerID: layer.id,
            active: newVisible,
          }),
        );
        return;
      }

      // Toggle all sublayers
      layer.sublayers.forEach(sl => {
        const gisSublayer = gisSublayerFor(sl);
        if (!gisSublayer) return;

        // Keep filtered sublayers always invisible
        if (filteredSublayerIds.includes(sl.uniqueId)) {
          gisSublayer.visible = false;
        } else {
          // Toggle valid sublayers
          gisSublayer.visible = newSublayerState;
        }
      });

      // For all layers, parent layer visibility should match sublayer state
      // If all sublayers are off, parent should be off too
      gisLayer.visible = newSublayerState;

      // Sync Redux state to match
      dispatch(
        updateLayer({
          layerID: layer.id,
          active: newSublayerState,
        }),
      );

      // Analytics tracking
      const onOffText = newSublayerState ? 'on' : 'off';
      gspx.trackEvent({
        name: 'map_turn_gis_layer',
        type: GPX_EVENT_TYPES.INFO,
        feature: GPX_FEATURES.LEGACY,
        capability: GPX_CAPABILITIES.LEGACY,
        route: GPX_ROUTES.MAP,
        additionalKeys: {
          legacy_category: 'Data Map',
          legacy_name: `Turn ${onOffText} ${layer.title}`,
          Category: 'GIS Service',
          'Launched date': new Date(),
          irpVersion,
          layerTitle: layer.title,
        },
      });

      trackToggleSupplementalLayer({
        layerName: layer.title,
        isVisible: newSublayerState,
        layerCategory: 'gis_service',
      });
    }, [
      dispatch,
      layer,
      irpVersion,
      filteredSublayerIds,
      onRemove,
      reportLoadError,
    ]);

    useEffect(() => {
      const gisLayer = gisLayerFor(layer);
      if (!gisLayer) return;

      gisLayer.visible = layer.active;
    }, [layer]);

    const sublayerControls = useMemo(() => {
      const visibleSublayers = layer.sublayers.filter(
        sl => !filteredSublayerIds.includes(sl.uniqueId),
      );

      // WFS + raster companion: collapse numbered Footprint/Image leaves into
      // one checkbox per role so toggling applies across the whole service.
      const hasCompanionRoles = visibleSublayers.some(sl =>
        isCompanionRoleTitle(sl.title),
      );
      if (hasCompanionRoles) {
        const footprints = visibleSublayers.filter(sl =>
          isCompanionFootprintTitle(sl.title),
        );
        const images = visibleSublayers.filter(sl =>
          isCompanionImageTitle(sl.title),
        );
        const other = visibleSublayers.filter(
          sl => !isCompanionRoleTitle(sl.title),
        );

        return (
          <>
            <CompanionRoleControl
              layerId={layer.id}
              label={COMPANION_IMAGE_TITLE}
              sublayers={images}
            />
            <CompanionRoleControl
              layerId={layer.id}
              label={COMPANION_FOOTPRINT_TITLE}
              sublayers={footprints}
            />
            {other.map(sl => (
              <GISSublayerControl
                layerId={layer.id}
                sublayer={sl}
                key={sl.uniqueId}
              />
            ))}
          </>
        );
      }

      return visibleSublayers.map(sl => (
        <GISSublayerControl
          layerId={layer.id}
          sublayer={sl}
          key={sl.uniqueId}
        />
      ));
    }, [layer.id, layer.sublayers, filteredSublayerIds]);

    if (isBasemapWithoutSublayers) {
      return null;
    }

    // Show loading indicator in label if layer is currently loading
    const displayLabel = isLoading
      ? `${layer.title} (Loading...)`
      : layer.title;
    const subtitle = layer.service?.attributes?.subtitle?.trim() || undefined;
    const removeLabel = `Remove "${layer.title}" from on-demand layers`;
    const removeButton = onRemove ? (
      <RemoveLayerButton
        className="on-demand-remove-btn"
        aria-label={removeLabel}
        onClick={e => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <XCloseIcon width={14} height={12} color="secondaryText" />
      </RemoveLayerButton>
    ) : null;

    if (layer.sublayers.length > 1) {
      const showLevelPicker = hasWmsCompanion(layer);
      return (
        <GISLayerAccordion
          title={
            <Checkbox
              label={displayLabel}
              description={subtitle}
              isChecked={layer.active}
              disabled={isDisabled}
              onChange={toggle}
            />
          }
          body={
            <>
              {showLevelPicker ? <CompanionLevelPicker layer={layer} /> : null}
              {sublayerControls}
            </>
          }
          headerAction={removeButton}
        />
      );
    }

    return (
      <RegularCheckboxWrapper>
        <LayerControlRow>
          <Box grow>
            <Checkbox
              label={displayLabel}
              description={subtitle}
              isChecked={layer.active}
              disabled={isDisabled}
              onChange={toggle}
            />
          </Box>
          {removeButton}
        </LayerControlRow>
      </RegularCheckboxWrapper>
    );
  },
);
